import { Stack, Typography, Box, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GavelIcon from '@mui/icons-material/Gavel';
import { AxisCard, NotCollected, AxisLoading, AxisError } from './ProductAxisCard';

// #108 Phase 1 — 제품 축의 규제준수 카드. 데이터: GET /api/compliance/status 를 제품 id 로 필터.
//
// 🔴 unknown 을 pass 로 접지 않는다. /compliance 전사 화면의 원칙을 그대로 옮긴다.
//   "확인 못 함" 이 초록으로 보이면 그 화면은 거짓말을 한다.
//   준수율 분모에서도 n/a 만 뺀다 — unknown 을 빼면 모르는 것이 사라져 비율이 부풀려진다.

const ST_LABEL = { pass: '준수', fail: '미준수', unknown: '확인 불가', 'n/a': '해당 없음' };

function stColor(theme, st) {
  switch (st) {
    case 'pass': return theme.palette.success.main;
    case 'fail': return theme.palette.error.main;
    case 'unknown': return theme.palette.text.disabled;
    default: return theme.palette.action.disabled;
  }
}

function gradeMeta(theme, g) {
  switch (g) {
    case 'red': return { color: theme.palette.error.main, label: '차단 항목 미준수' };
    case 'amber': return { color: theme.palette.warning.main, label: '미준수 있음' };
    case 'grey': return { color: theme.palette.text.disabled, label: '확인 불가 우세' };
    case 'green': return { color: theme.palette.success.main, label: '준수' };
    default: return { color: theme.palette.text.disabled, label: '판정 없음' };
  }
}

export default function ProductComplianceCard({ productId, state }) {
  const theme = useTheme();
  const { data, error, loading } = state;
  const row = data?.products?.find((p) => p.product === productId) || null;

  const SOURCE = 'cloud-compliance/reports/dashboard/compliance-feed.json';
  const SOURCE_URL = 'https://github.com/rayrndsw/cloud-compliance/blob/main/reports/dashboard/compliance-feed.json';
  const generated = data?.generated ? String(data.generated).slice(0, 10) : null;

  // 피드가 스스로 신선도 기준을 싣고 온다 (stale_after_days).
  const stale = (() => {
    if (!data?.generated || !data?.stale_after_days) return false;
    const age = (Date.now() - Date.parse(`${String(data.generated).slice(0, 10)}T00:00:00Z`)) / 86400000;
    return age > Number(data.stale_after_days);
  })();

  const card = (children, action) => (
    <AxisCard
      title="규제 준수 (RCS)"
      icon={<GavelIcon sx={{ fontSize: 19, color: 'text.secondary' }} />}
      source={SOURCE}
      sourceUrl={SOURCE_URL}
      updatedAt={generated}
      stale={stale}
      action={action}
    >
      {children}
    </AxisCard>
  );

  if (loading) return card(<AxisLoading />);
  if (error) return card(<AxisError message="규제 준수 현황을 불러오지 못했습니다" hint={error} />);
  if (!row) {
    return card(
      <NotCollected reason="이 제품이 RCS 점검 피드에 없습니다 — 주간 검사 대상에 아직 포함되지 않았을 수 있습니다." />,
    );
  }

  const s = row.summary || {};
  const gm = gradeMeta(theme, row.grade);
  // 분모에서 n/a 만 뺀다.
  const denom = (s.pass || 0) + (s.fail || 0) + (s.unknown || 0);
  const pct = denom ? Math.round(((s.pass || 0) / denom) * 100) : 0;

  return card(
    <Stack spacing={1.25}>
      {/* pass / fail / unknown 비율 바 — unknown 이 차지하는 폭이 곧 관측 가능성 지표다 */}
      <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', width: '100%' }}>
        {['pass', 'fail', 'unknown'].map((k) => {
          const v = s[k] || 0;
          if (!v || !denom) return null;
          return (
            <Tooltip key={k} title={`${ST_LABEL[k]} ${v}`}>
              <Box sx={{ width: `${(v / denom) * 100}%`, bgcolor: stColor(theme, k) }} />
            </Tooltip>
          );
        })}
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={2}>
        {['pass', 'fail', 'unknown', 'n/a'].map((k) => (
          <Box key={k} sx={{ minWidth: 58 }}>
            <Typography sx={{
              fontSize: 22, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.15,
              color: (s[k === 'n/a' ? 'na' : k] || 0) > 0 ? stColor(theme, k) : 'text.primary',
            }}>
              {s[k === 'n/a' ? 'na' : k] ?? 0}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{ST_LABEL[k]}</Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ fontSize: 12, color: 'text.disabled' }}>
        <span>준수율 <b>{pct}%</b></span>
        <Tooltip title="확인 불가(unknown)를 분모에서 빼지 않습니다 — 빼면 모르는 것이 사라져 비율이 부풀려집니다">
          <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help' }}>분모 = 준수+미준수+확인 불가</Box>
        </Tooltip>
        {(s.block_fail || 0) > 0 && (
          <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>차단 게이트 실패 {s.block_fail}</Box>
        )}
      </Stack>

      {(s.unknown || 0) > 0 && (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.6 }}>
          확인 불가 <b>{s.unknown}</b>건은 <b>준수가 아닙니다</b> — 관측하지 못한 항목입니다.
        </Typography>
      )}
    </Stack>,
    <Chip size="small" label={gm.label}
      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: gm.color, color: '#fff' }} />,
  );
}
